import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDrawerToggleResult, MatSidenav } from '@angular/material/sidenav';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';

import { AuthenticatedUserService, LanguageService } from 'shared/services';
import { MenuListItem } from 'fc-ui-components';

import { MobilePlatformNavigationMenuItem, PlatformToolbarLink } from '../types';
import { MainNavigationService } from '../main-navigation.service';

@Component({
    selector: 'fc-mobile-menu',
    templateUrl: './mobile-menu.component.html',
    styleUrls: ['./mobile-menu.component.less'],
    standalone: false
})
export class MobileMenuComponent implements OnInit {
    @ViewChild(MatSidenav) private sideNav: MatSidenav;
    @Input() private items: MobilePlatformNavigationMenuItem[];

    visibleMenuList: Observable<(MobilePlatformNavigationMenuItem | PlatformToolbarLink | MenuListItem)[]>;
    showMenuChildItems = new BehaviorSubject<boolean>(false);
    showSettingsMenu = new BehaviorSubject<boolean>(false);
    showSecondaryMenu: Observable<boolean>;

    private selectedMenuItem = new BehaviorSubject<
        MobilePlatformNavigationMenuItem | PlatformToolbarLink | MenuListItem
    >(null);

    constructor(
        public languageService: LanguageService,
        private authenticatedUserService: AuthenticatedUserService,
        private mainNavigationService: MainNavigationService
    ) {}

    ngOnInit() {
        const settingsMenuItems = combineLatest([this.showSettingsMenu, this.mainNavigationService.settingsList]).pipe(
            map(([showSettingsMenu, settingsList]) => (showSettingsMenu && settingsList.length ? settingsList : []))
        );

        const subMenuItems = combineLatest([this.showMenuChildItems, this.mainNavigationService.toolbarLinks]).pipe(
            map(([showMenuChildItems, toolbarLinks]) => (showMenuChildItems && toolbarLinks.length ? toolbarLinks : []))
        );

        this.showSecondaryMenu = combineLatest([settingsMenuItems, subMenuItems]).pipe(
            map(([settingsMenuItems, subMenuItems]) => !!settingsMenuItems.length || !!subMenuItems.length)
        );

        this.visibleMenuList = combineLatest([settingsMenuItems, subMenuItems]).pipe(
            switchMap(([settingsMenuItems, subMenuItems]) => {
                const results = settingsMenuItems.length
                    ? settingsMenuItems
                    : subMenuItems.length
                      ? subMenuItems.map(
                            (item) =>
                                ({
                                    ...item,
                                    route: {
                                        ...item.route,
                                        routerLinkActiveOptions: {
                                            matrixParams: 'exact',
                                            queryParams: 'ignored',
                                            paths: 'exact',
                                            fragment: 'exact'
                                        }
                                    }
                                }) as PlatformToolbarLink
                        )
                      : this.items;
                return of(results);
            })
        );
    }

    logout() {
        this.authenticatedUserService.logout();
    }

    toggle(): Promise<MatDrawerToggleResult> {
        return this.sideNav.toggle();
    }

    onMenuItemClick(menuItem: MobilePlatformNavigationMenuItem | PlatformToolbarLink | MenuListItem) {
        this.selectedMenuItem.next(menuItem);

        if (_.get(menuItem, 'hasChildren')) {
            this.showMenuChildItems.next(true);
        } else {
            this.closeMenu();
        }
    }

    closeMenu() {
        this.sideNav.toggle(false);
    }

    goBackToMainMenu() {
        this.showSettingsMenu.next(false);
        this.showMenuChildItems.next(false);
    }

    onMenuActiveChange(activeMenuItem: MobilePlatformNavigationMenuItem | PlatformToolbarLink | MenuListItem) {
        const activeMenuItemLabel = _.get(activeMenuItem, 'label', _.get(activeMenuItem, 'text'));

        const selectedMenuItemLabel = _.get(
            this.selectedMenuItem.value,
            'label',
            _.get(this.selectedMenuItem.value, 'text')
        );

        const isParent = _.get(this.selectedMenuItem.value, 'hasChildren');

        // If the selected menu item is a parent, we don't want to change the page title
        if (isParent && activeMenuItemLabel === selectedMenuItemLabel) {
            return;
        }

        const pageTitle = !isParent && this.selectedMenuItem.value ? selectedMenuItemLabel : activeMenuItemLabel;

        this.mainNavigationService.setPageTitle(pageTitle);
    }

    openSettings() {
        this.showSettingsMenu.next(true);
    }
}
